/**
 * V2.0.75 — Lightweight blueprint diff.
 *
 * Compares two blueprints and returns a compact summary of what
 * changed. Used to show a "// AI CHANGES" chip after each successful
 * chat turn so the user knows exactly what the AI did instead of
 * eyeballing the whole floor plan to find edits.
 *
 * Not a full deep-diff — we count adds/removes/modifies by ID across
 * the categories most likely to matter to a homeowner:
 *   • rooms (add, remove, resize, rename)
 *   • walls (add, remove)
 *   • doors + windows (add, remove)
 *   • fixtures / furniture (add, remove)
 *
 * Two rooms with the same `id` but different dimensions count as a
 * "resize"; different `name` counts as a "rename"; both changing counts
 * as one "modify". Modifies never double-count.
 */

const CATEGORIES = [
  { key: "rooms", label: "room" },
  { key: "walls", label: "wall" },
  { key: "doors", label: "door" },
  { key: "windows", label: "window" },
  { key: "fixtures", label: "fixture" },
  { key: "furniture", label: "furniture" },
];

function collectByCategory(bp, key) {
  if (!bp || !Array.isArray(bp.floors)) return [];
  const out = [];
  for (const f of bp.floors) {
    const arr = (f && f[key]) || [];
    if (Array.isArray(arr)) out.push(...arr);
  }
  return out;
}

function isMaterialChange(before, after) {
  // Ignore purely-decorative changes; only flag structural / label changes.
  if (before.name !== after.name) return true;
  if (before.type !== after.type) return true;
  const dims = ["width", "depth", "height", "x", "z"];
  for (const k of dims) {
    if (before[k] !== undefined && after[k] !== undefined) {
      if (Math.abs(Number(before[k]) - Number(after[k])) > 0.15) return true;
    }
  }
  return false;
}

export function computeBlueprintDiff(before, after) {
  if (!before || !after) {
    return { hasChanges: false, summary: "", added: {}, removed: {}, modified: {} };
  }
  const added = {};
  const removed = {};
  const modified = {};
  let hasChanges = false;

  for (const { key } of CATEGORIES) {
    const beforeArr = collectByCategory(before, key);
    const afterArr = collectByCategory(after, key);
    const beforeById = new Map(beforeArr.filter((x) => x && x.id).map((x) => [x.id, x]));
    const afterById = new Map(afterArr.filter((x) => x && x.id).map((x) => [x.id, x]));

    let a = 0;
    let r = 0;
    let m = 0;

    // Track additions + modifications
    for (const [id, item] of afterById) {
      if (!beforeById.has(id)) {
        a++;
      } else if (isMaterialChange(beforeById.get(id), item)) {
        m++;
      }
    }
    // Track removals
    for (const id of beforeById.keys()) {
      if (!afterById.has(id)) r++;
    }
    // Items without ids: fall back to count-delta as a crude proxy.
    const bNoId = beforeArr.filter((x) => !x || !x.id).length;
    const aNoId = afterArr.filter((x) => !x || !x.id).length;
    if (aNoId > bNoId) a += aNoId - bNoId;
    else if (bNoId > aNoId) r += bNoId - aNoId;

    if (a || r || m) {
      hasChanges = true;
      if (a) added[key] = a;
      if (r) removed[key] = r;
      if (m) modified[key] = m;
    }
  }

  // Build a human-readable one-liner. Prioritise the highest-impact
  // categories so short chips read naturally.
  const parts = [];
  const pushCat = (bucket, verb) => {
    for (const { key, label } of CATEGORIES) {
      const n = bucket[key];
      if (!n) continue;
      const plural = n === 1 ? label : `${label}s`;
      parts.push(`${verb} ${n} ${plural}`);
    }
  };
  pushCat(added, "added");
  pushCat(removed, "removed");
  pushCat(modified, "modified");

  return {
    hasChanges,
    summary: parts.join(" · "),
    added,
    removed,
    modified,
  };
}
